import { BusinessError, request } from '@/functions/data-access';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownWideShort,
  faArrowUpShortWide,
  faCopy,
  faPlay,
  faTrash,
} from '@fortawesome/sharp-solid-svg-icons';
import { SCAdvanceTableInteractive, SCInputString, SCSelect, SCView } from '@lib/sc-eone-ui';
import { Button } from 'eone-ui/buttons/button';
import { Modal } from 'eone-ui/modal/modal';
import { ModalButtonType, ModalWidth } from 'eone-ui/modal/modal-constants';
import { TableFieldType } from 'eone-ui/tables/table-constants';
import { useMemo, useState } from 'react';
import {
  buildWhereText,
  fieldCategory,
  hasFilters,
  shouldQuote,
  type FilterGroup,
  type FilterNode,
} from './filters';
import { METADATA_BROWSER_TID, type SubquerySelection } from './types';

// Kendo grid treats a dotted field name as a nested path — flatten our dotted columns
// (Account.Name, Rel.field) to a safe key so row[field] resolves directly.
const safeKey = (col: string) => col.replace(/[^A-Za-z0-9]/g, '_');

const TID = `${METADATA_BROWSER_TID}.soql`;

// Serialize the client filter tree to the server's SalesforceFilterNode shape. The server does the
// authoritative quoting/escaping; we only forward the field type's quote decision.
function toServerFilter(node: FilterNode): any {
  if (node.kind === 'group') {
    return {
      type: 'group',
      combinator: node.combinator,
      children: node.children.map(toServerFilter),
    };
  }
  return {
    type: 'condition',
    field: node.field,
    operator: node.operator,
    quote: shouldQuote(fieldCategory(node.type)),
    valueMode: node.valueMode,
    value: node.value,
    values: node.values,
  };
}

type Status = { kind: 'idle' } | { kind: 'ok'; msg: string } | { kind: 'err'; msg: string };

interface PreviewResult {
  soql: string;
  columns: string[];
  rows: Record<string, string>[];
}

type Dir = 'ASC' | 'DESC';
type Nulls = '' | 'FIRST' | 'LAST';
interface OrderByClause {
  field: string; // one of the selected dot-paths
  dir: Dir;
  nulls: Nulls;
}

interface SoqlPanelProps {
  connectionId: string;
  baseObject: string | null;
  fields: string[]; // dot-paths relative to the base object (e.g. "Id", "Account.Name")
  subqueries: Record<string, SubquerySelection>; // child-relationship subqueries, keyed by rel name
  where: FilterGroup;
  onClear: () => void; // clears the parent-held selection (fields / subqueries / WHERE)
}

export function SoqlPanel({
  connectionId,
  baseObject,
  fields,
  subqueries,
  where,
  onClear,
}: SoqlPanelProps) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [orderBy, setOrderBy] = useState<OrderByClause[]>([]);
  const [limit, setLimit] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const subqueryEntries = Object.entries(subqueries);
  const subqueryFieldCount = subqueryEntries.reduce((n, [, v]) => n + v.fields.length, 0);
  const totalCount = fields.length + subqueryFieldCount;

  const selectParts = [
    ...fields,
    ...subqueryEntries.map(([rel, v]) => `(SELECT ${v.fields.join(', ')} FROM ${rel})`),
  ];
  const hasSelection = !!baseObject && selectParts.length > 0;
  const whereText = buildWhereText(where);

  // ORDER BY can only reference selected root/parent fields; drop clauses whose field was unticked.
  const activeOrderBy = useMemo(
    () => orderBy.filter((o) => fields.includes(o.field)),
    [orderBy, fields],
  );
  const sortableOptions = fields.filter((f) => !activeOrderBy.some((o) => o.field === f));
  const limitNum = /^\d+$/.test(limit.trim()) && Number(limit) > 0 ? Number(limit) : null;

  const orderByText = activeOrderBy
    .map((o) => `${o.field} ${o.dir}${o.nulls ? ` NULLS ${o.nulls}` : ''}`)
    .join(', ');

  const addOrderBy = (field: string) => {
    if (!field) return;
    setOrderBy((prev) => [...prev, { field, dir: 'ASC', nulls: '' }]);
  };
  const removeOrderBy = (field: string) =>
    setOrderBy((prev) => prev.filter((o) => o.field !== field));
  const setOrderByProp = (field: string, patch: Partial<OrderByClause>) =>
    setOrderBy((prev) => prev.map((o) => (o.field === field ? { ...o, ...patch } : o)));

  // Shared build/preview payload — the selection the server assembles into SOQL.
  const requestData = () => ({
    baseObject,
    fields,
    subqueries: subqueryEntries.map(([relationshipName, v]) => ({
      relationshipName,
      fields: v.fields,
    })),
    where: hasFilters(where) ? toServerFilter(where) : null,
    orderBy: activeOrderBy.map((o) => ({
      field: o.field,
      direction: o.dir,
      nulls: o.nulls || null,
    })),
    limit: limitNum,
  });

  const onValidateCopy = async () => {
    if (!hasSelection || busy) return;
    setBusy(true);
    try {
      // The server assembles the canonical SOQL and validates it against Salesforce.
      const soql = await request<string>({
        url: `/salesforce/build-soql/${connectionId}`,
        method: 'POST',
        data: requestData(),
      });
      await navigator.clipboard?.writeText(soql);
      setStatus({ kind: 'ok', msg: 'Validated & copied to clipboard.' });
    } catch (error) {
      setStatus({
        kind: 'err',
        msg: error instanceof BusinessError ? error.message : 'Validation failed.',
      });
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async () => {
    if (!hasSelection || previewBusy) return;
    setPreviewBusy(true);
    setStatus({ kind: 'idle' });
    try {
      const res = await request<PreviewResult>({
        url: `/salesforce/preview-soql/${connectionId}`,
        method: 'POST',
        data: requestData(),
      });
      setPreview(res);
    } catch (error) {
      setStatus({
        kind: 'err',
        msg: error instanceof BusinessError ? error.message : 'Preview failed.',
      });
    } finally {
      setPreviewBusy(false);
    }
  };

  // Preview grid columns/rows for SCAdvanceTableInteractive (dotted columns → safe field keys).
  const previewTable = useMemo(() => {
    if (!preview) return { columns: [] as any[], rows: [] as any[] };
    const columns = preview.columns.map((c) => ({
      Label: c,
      FieldName: safeKey(c),
      FieldType: TableFieldType.String,
      Sortable: true,
      Searchable: true,
    }));
    const rows = preview.rows.map((r, i) => {
      const o: Record<string, unknown> = { __id: i };
      preview.columns.forEach((c) => {
        o[safeKey(c)] = r[c] ?? '';
      });
      return o;
    });
    return { columns, rows };
  }, [preview]);

  const canClear = hasSelection || activeOrderBy.length > 0 || limit !== '' || hasFilters(where);
  const handleClear = () => {
    setOrderBy([]);
    setLimit('');
    setStatus({ kind: 'idle' });
    setPreview(null);
    onClear();
  };

  return (
    <SCView flexCol className="sfmb-panel sfmb-soql" DataTestId={TID}>
      <SCView flex justifyBetween itemsCenter p={3} borderB gap={2}>
        <SCView fontBold textSm>
          SOQL query
        </SCView>
        <SCView flex itemsCenter gap={2}>
          <SCView textXs textDesc DataTestId={`${TID}.count`}>
            {totalCount} field{totalCount === 1 ? '' : 's'} selected
            {subqueryEntries.length > 0 &&
              ` · ${subqueryEntries.length} subquer${subqueryEntries.length === 1 ? 'y' : 'ies'}`}
          </SCView>
          <button
            type="button"
            className="sfmb-iconbtn"
            disabled={!canClear}
            onClick={handleClear}
            title="Clear the query"
            data-testid={`${TID}.clear`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </SCView>
      </SCView>

      <SCView
        flex1
        flex
        flexCol
        gap={3}
        p={3}
        style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <pre className="sfmb-code" data-testid={`${TID}.preview`}>
          {hasSelection ? (
            <>
              <span className="sfmb-kw">SELECT</span> {selectParts.join(', ')}
              {'\n'}
              <span className="sfmb-kw">FROM</span> {baseObject}
              {whereText && (
                <>
                  {'\n'}
                  <span className="sfmb-kw">WHERE</span> {whereText}
                </>
              )}
              {orderByText && (
                <>
                  {'\n'}
                  <span className="sfmb-kw">ORDER BY</span> {orderByText}
                </>
              )}
              {limitNum && (
                <>
                  {'\n'}
                  <span className="sfmb-kw">LIMIT</span> {limitNum}
                </>
              )}
            </>
          ) : (
            <span className="sfmb-code-empty">Tick fields on the left to build a query…</span>
          )}
        </pre>

        {hasSelection && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* ORDER BY */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <SCView textXs fontBold textDesc>
                ORDER BY
              </SCView>
              {sortableOptions.length > 0 && (
                <SCSelect
                  options={sortableOptions.map((f) => ({ value: f, label: f }))}
                  value={undefined}
                  placeholder="+ Add sort…"
                  search
                  onChange={(v) => {
                    if (v) addOrderBy(String(v));
                  }}
                  DataTestId={`${TID}.order-by.add`}
                />
              )}
            </div>
            {activeOrderBy.map((o) => (
              <SCView
                key={o.field}
                flexCol
                gap={1}
                className="sfmb-ob-row"
                DataTestId={`${TID}.order-by.${o.field}`}
              >
                <SCView flex itemsCenter justifyBetween gap={2}>
                  <SCView flex1 textXs fontBold className="sfmb-mono" truncate title={o.field}>
                    {o.field}
                  </SCView>
                  <button
                    type="button"
                    className="sfmb-icon-btn"
                    onClick={() => removeOrderBy(o.field)}
                    title="Remove"
                    data-testid={`${TID}.order-by.${o.field}.remove`}
                  >
                    ×
                  </button>
                </SCView>
                <SCView flex itemsCenter gap={2}>
                  <button
                    type="button"
                    className="sfmb-iconbtn"
                    onClick={() =>
                      setOrderByProp(o.field, { dir: o.dir === 'ASC' ? 'DESC' : 'ASC' })
                    }
                    title={
                      o.dir === 'ASC'
                        ? 'Ascending — click for descending'
                        : 'Descending — click for ascending'
                    }
                    data-testid={`${TID}.order-by.${o.field}.dir`}
                  >
                    <FontAwesomeIcon
                      icon={o.dir === 'ASC' ? faArrowUpShortWide : faArrowDownWideShort}
                    />
                  </button>
                  <SCView flex1>
                    <SCSelect
                      options={[
                        { value: '', label: 'Nulls default' },
                        { value: 'FIRST', label: 'Nulls first' },
                        { value: 'LAST', label: 'Nulls last' },
                      ]}
                      value={o.nulls}
                      onChange={(v) => setOrderByProp(o.field, { nulls: (v ?? '') as Nulls })}
                      DataTestId={`${TID}.order-by.${o.field}.nulls`}
                    />
                  </SCView>
                </SCView>
              </SCView>
            ))}

            {/* LIMIT */}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              LIMIT
              <SCInputString
                Value={limit}
                OnChange={(v: string) => setLimit(v.replace(/[^\d]/g, ''))}
                Placeholder="none"
                DataTestId={`${TID}.limit`}
              />
            </div>
          </div>
        )}
      </SCView>

      <SCView flex itemsCenter gap={2} p={3} borderT flexWrap>
        <Button
          Label="Preview"
          Icon={faPlay}
          Disabled={!hasSelection}
          Loading={previewBusy}
          OnClick={onPreview}
          Class="eone-ui-button-container-secondary"
          DataTestId={`${TID}.preview`}
        />
        <Button
          Label="Validate & Copy"
          Icon={faCopy}
          Disabled={!hasSelection}
          Loading={busy}
          OnClick={onValidateCopy}
          Class="eone-ui-button-container-primary"
          DataTestId={`${TID}.copy`}
        />
        {status.kind === 'ok' && (
          <SCView
            textXs
            style={{ color: 'var(--sc-text-color-success)' }}
            DataTestId={`${TID}.status`}
          >
            {status.msg}
          </SCView>
        )}
        {status.kind === 'err' && (
          <SCView textXs textError DataTestId={`${TID}.status`}>
            {status.msg}
          </SCView>
        )}
      </SCView>

      <SCView textXs textDesc px={3} pb={3}>
        Preview only — the query is assembled &amp; validated on the server.
      </SCView>

      <Modal
        Show={!!preview}
        Close={() => setPreview(null)}
        Title="Preview"
        Width={ModalWidth.ExtraWide}
        FooterButtons={[
          {
            Label: 'Close',
            Type: ModalButtonType.Secondary,
            Visible: true,
            OnClick: () => setPreview(null),
          },
        ]}
        DataTestId={`${TID}.preview-modal`}
      >
        <div className="sfmb-preview-modal-body" style={{ height: '60vh', overflow: 'hidden' }}>
          {previewTable.rows.length === 0 ? (
            <SCView p={4} textDesc>
              No rows returned.
            </SCView>
          ) : (
            <SCAdvanceTableInteractive
              Id="salesforce-metadata-preview"
              Search
              Columns={previewTable.columns}
              Rows={previewTable.rows}
              IdField="__id"
              EmptyMessage="No rows"
              ColumnReorderable={false}
              ColumnResizable
              DataTestId={`${TID}.preview-table`}
            />
          )}
        </div>
      </Modal>
    </SCView>
  );
}
